<script setup>
import { computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import SlotChatList from '../../../components/SlotChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';

const props = defineProps({
  inboxId: {
    type: [String, Number],
    default: 0,
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();

const conversationId = computed(() => route.params.id || 0);
const { uiSettings } = useUISettings();

const chatList = useMapGetter('getAllConversations');
const currentChat = useMapGetter('getSelectedChat');

const isAgentModeRoute = computed(
  () => route.name === 'agent_mode' || route.name === 'agent_mode_conversation'
);

const isOnExpandedLayout = computed(() => {
  const {
    LAYOUT_TYPES: { CONDENSED },
  } = wootConstants;
  const { conversation_display_type: type = CONDENSED } = uiSettings.value;
  return type !== CONDENSED;
});

const showConversationList = computed(() => {
  if (isAgentModeRoute.value) {
    return isOnExpandedLayout.value ? !conversationId.value : true;
  }
  return isOnExpandedLayout.value ? !conversationId.value : true;
});

const showMessageView = computed(() => {
  if (isAgentModeRoute.value) {
    return !!conversationId.value;
  }
  return conversationId.value ? true : !isOnExpandedLayout.value;
});

const showEmptyState = computed(() => {
  return isAgentModeRoute.value && !conversationId.value;
});

const shouldShowSidebar = computed(() => {
  if (!currentChat.value.id) return false;
  const { is_contact_sidebar_open: isOpen } = uiSettings.value;
  return isOpen;
});

const findConversation = () => {
  const id = parseInt(conversationId.value, 10);
  return chatList.value.find(c => c.id === id);
};

const setActiveChat = async () => {
  if (conversationId.value) {
    const selected = findConversation();
    if (!selected) return;
    const { messageId } = route.query;
    await store.dispatch('setActiveChat', { data: selected, after: messageId });
    emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
  } else {
    store.dispatch('clearSelectedState');
  }
};

const fetchConversationById = async () => {
  if (!conversationId.value) {
    store.dispatch('clearSelectedState');
    return;
  }

  store.dispatch('clearSelectedState');
  const existingChat = findConversation();

  if (existingChat) {
    await setActiveChat();
    return;
  }

  await store.dispatch('getConversation', conversationId.value);
  await setActiveChat();
};

const handleSelectConversation = conversation => {
  if (isAgentModeRoute.value) {
    router.push({
      name: 'agent_mode_conversation',
      params: { id: conversation.id },
    });
  }
};

watch(
  conversationId,
  () => {
    fetchConversationById();
  },
  { immediate: true }
);

watch(
  () => chatList.value.length,
  () => {
    setActiveChat();
  }
);

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('classifications/get');
  store.dispatch('portals/index');
  store.dispatch('setActiveInbox', props.inboxId);
  setActiveChat();
  if (!conversationId.value) {
    store.dispatch('clearSelectedState');
  }
});
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <SlotChatList
      :show-conversation-list="showConversationList"
      :active-conversation-id="conversationId"
      @select-conversation="handleSelectConversation"
    />

    <!-- Empty state when no conversation selected in agent mode -->
    <div
      v-if="showEmptyState"
      class="flex flex-col items-center justify-center flex-1 min-w-0 bg-n-background"
    >
      <fluent-icon icon="chat" size="48" class="text-n-slate-10 mb-4" />
      <h3 class="text-lg font-medium text-n-slate-12 mb-2">
        {{ $t('AGENT_MODE.SELECT_CONVERSATION_TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-10 text-center max-w-md">
        {{ $t('AGENT_MODE.SELECT_CONVERSATION_DESCRIPTION') }}
      </p>
    </div>

    <ConversationBox
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id" />
    </ConversationBox>

    <ConversationSidebar v-if="shouldShowSidebar" :current-chat="currentChat" />
    <CmdBarConversationSnooze />
  </section>
</template>
